import type { Columns, TableToolbarFilterConfig } from '@bitstopco/bitstop-theme';

import type { TransactionsSale, WithTags } from '@/types';

import {
  CUSTOMERS_FILTERS_CONFIG,
  CUSTOMER_SALES_COLUMNS,
} from '@/constants/app/customers/customer';
import { SALES_COLUMNS as MACHINE_SALES_COLUMNS } from '@/constants/app/machine';
import { SALES_COLUMNS, TRANSACTIONS_FILTERS_CONFIG } from '@/constants/app/transactions';

/**
 * Check if user has Cash Analyst role
 */
export const isCashAnalyst = (userRoleName?: string): boolean => {
  return userRoleName?.toLowerCase() === 'cash analyst';
};

/**
 * Filter sales columns based on user role
 * For cash analyst role, omit Address, Transaction Hash, and Customer columns
 */
export const getFilteredSalesColumns = (
  userRoleName?: string,
): Columns<WithTags<TransactionsSale>> => {
  if (!isCashAnalyst(userRoleName)) {
    return SALES_COLUMNS;
  }

  // Filter out specific columns for cash analyst role
  const filteredItems = SALES_COLUMNS.items.filter((item) => {
    const value = item.value;
    // Omit Address, Transaction Hash, and Customer columns
    return value !== 'address' && value !== 'transaction_hash' && value !== 'customer_id';
  });

  return {
    ...SALES_COLUMNS,
    items: filteredItems,
  };
};

/**
 * Filter machine sales columns based on user role
 * For cash analyst role, omit Address, Transaction Hash, and Customer columns
 */
export const getFilteredMachineSalesColumns = (
  userRoleName?: string,
): Columns<TransactionsSale> => {
  if (!isCashAnalyst(userRoleName)) {
    return MACHINE_SALES_COLUMNS;
  }

  // Filter out specific columns for cash analyst role
  const filteredItems = MACHINE_SALES_COLUMNS.items.filter((item) => {
    const value = item.value;
    // Omit Address, Transaction Hash, and Customer columns
    return value !== 'address' && value !== 'transaction_hash' && value !== 'customer_id';
  });

  return {
    ...MACHINE_SALES_COLUMNS,
    items: filteredItems,
  };
};

/**
 * Filter sales filters based on user role
 * For cash analyst role, omit customer_id filter
 */
export const getFilteredSalesFilters = (userRoleName?: string): TableToolbarFilterConfig[] => {
  if (!isCashAnalyst(userRoleName)) {
    return TRANSACTIONS_FILTERS_CONFIG;
  }

  // Filter out customer_id filter for cash analyst role
  const filteredFilters = TRANSACTIONS_FILTERS_CONFIG.filter((filter) => {
    return filter.property !== 'customer_id';
  });

  return filteredFilters;
};

/**
 * Get filtered sales columns and filters based on user role
 * Returns both filtered columns and filters in a single object
 */
export const getFilteredSalesConfig = (userRoleName?: string) => {
  return {
    columns: getFilteredSalesColumns(userRoleName),
    filters: getFilteredSalesFilters(userRoleName),
  };
};

/**
 * Get filtered machine sales columns and filters based on user role
 * Returns both filtered columns and filters in a single object
 */
export const getFilteredMachineSalesConfig = (userRoleName?: string) => {
  return {
    columns: getFilteredMachineSalesColumns(userRoleName),
    filters: getFilteredSalesFilters(userRoleName),
  };
};

/**
 * Filter customer sales columns based on user role
 * For cash analyst role, omit Address, Transaction Hash, and Customer columns
 */
export const getFilteredCustomerSalesColumns = (
  userRoleName?: string,
): Columns<TransactionsSale> => {
  if (!isCashAnalyst(userRoleName)) {
    return CUSTOMER_SALES_COLUMNS;
  }

  // Filter out specific columns for cash analyst role
  const filteredItems = CUSTOMER_SALES_COLUMNS.items.filter((item) => {
    const value = item.value;
    // Omit Address, Transaction Hash, and Customer columns
    return value !== 'address' && value !== 'transaction_hash' && value !== 'customer_id';
  });

  return {
    ...CUSTOMER_SALES_COLUMNS,
    items: filteredItems,
  };
};

/**
 * Filter customer sales filters based on user role
 * For cash analyst role, omit customer_id filter (though this shouldn't be present in customer filters)
 */
export const getFilteredCustomerSalesFilters = (
  userRoleName?: string,
): TableToolbarFilterConfig[] => {
  if (!isCashAnalyst(userRoleName)) {
    return CUSTOMERS_FILTERS_CONFIG;
  }

  // Filter out customer_id filter for cash analyst role (though it shouldn't be present)
  const filteredFilters = CUSTOMERS_FILTERS_CONFIG.filter((filter) => {
    return filter.property !== 'customer_id';
  });

  return filteredFilters;
};

/**
 * Get filtered customer sales columns and filters based on user role
 * Returns both filtered columns and filters in a single object
 */
export const getFilteredCustomerSalesConfig = (userRoleName?: string) => {
  return {
    columns: getFilteredCustomerSalesColumns(userRoleName),
    filters: getFilteredCustomerSalesFilters(userRoleName),
  };
};
